import { useMemo, useState } from 'react';
import type { ColumnDef, VisualizationConfig } from '@deckspace/types';
import { formatUnitValue } from '../../utils/format-unit-value.js';
import { prepTableSeries, type TableMapping } from './table-utils.js';

interface Props {
  rows: Record<string, unknown>[];
  columnDefs: ColumnDef[];
  mapping: TableMapping;
  config: VisualizationConfig;
  width?: number;
  height?: number;
}

/**
 * Render a column header. Uses `columnDef.label` when present; otherwise
 * title-cases the storage `name` (`weight_lbs` → `Weight Lbs`). Authors use
 * `label` to fix acronyms (`mrr` → `MRR`) or unit suffixes (`p50_ms` → `p50 (ms)`).
 */
export function formatColumnHeader(columnDef: ColumnDef): string {
  if (columnDef.label) return columnDef.label;
  return columnDef.name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function findColumnDef(columnDefs: ColumnDef[], name: string): ColumnDef | undefined {
  return columnDefs.find((d) => d.name === name);
}

export function Table({ rows, columnDefs, mapping, config: _config }: Props) {
  // Interactive sort overrides mapping sort. Click cycles asc → desc → off.
  const [sortColumn, setSortColumn] = useState<string | null>(mapping.sortColumn ?? null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    mapping.sortDirection ?? 'asc'
  );

  const effectiveMapping = useMemo<TableMapping>(
    () => ({
      ...mapping,
      sortColumn: sortColumn ?? undefined,
      sortDirection: sortColumn ? sortDirection : undefined,
    }),
    [mapping, sortColumn, sortDirection]
  );

  const { columns, rows: prepared } = useMemo(
    () =>
      rows.length === 0
        ? {
            columns: mapping.visibleColumns?.length
              ? mapping.visibleColumns.filter((c) => columnDefs.some((d) => d.name === c))
              : columnDefs.map((d) => d.name),
            rows: [] as (string | number)[][],
          }
        : prepTableSeries(rows, columnDefs, effectiveMapping),
    [rows, columnDefs, mapping.visibleColumns, effectiveMapping]
  );

  if (rows.length === 0) {
    return <div className="text-muted-foreground">No data available</div>;
  }

  const handleSort = (column: string) => {
    if (sortColumn !== column) {
      setSortColumn(column);
      setSortDirection('asc');
      return;
    }
    if (sortDirection === 'asc') {
      setSortDirection('desc');
      return;
    }
    setSortColumn(null);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="overflow-auto flex-1 min-h-0 rounded-md border border-border">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted sticky top-0 z-10 border-b-2 border-border">
            <tr>
              {columns.map((column) => {
                const def = findColumnDef(columnDefs, column);
                const header = def
                  ? formatColumnHeader(def)
                  : column.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                return (
                  <th
                    key={column}
                    scope="col"
                    onClick={() => handleSort(column)}
                    className="px-4 py-3 text-left text-sm font-semibold text-foreground cursor-pointer hover:bg-secondary select-none bg-muted"
                  >
                    <span className="flex items-center gap-1">
                      {header}
                      {sortColumn === column && (
                        <span className="text-primary">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {prepared.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-background">
                {row.map((cell, cellIndex) => {
                  const columnName = columns[cellIndex];
                  const def = columnName ? findColumnDef(columnDefs, columnName) : undefined;
                  const display = def
                    ? formatUnitValue(cell, def.type, def.currency, def.unit)
                    : typeof cell === 'number'
                      ? cell.toLocaleString()
                      : cell;
                  return (
                    <td
                      key={cellIndex}
                      className="px-4 py-3 whitespace-nowrap text-sm text-foreground"
                    >
                      {display}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
